import React, { useState, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { BRAND, SERVICES } from "../lib/brand";
import { Phone, Menu, X, ChevronDown } from "lucide-react";

const navLinks = [
  { href: "/about-us", label: "About", type: "route" },
  { href: "/#why", label: "Why Us", type: "anchor" },
  { href: "/#areas", label: "Service Area", type: "anchor" },
  { href: "/#reviews", label: "Reviews", type: "anchor" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const closeTimer = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const openServices = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setServicesOpen(true);
  };
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setServicesOpen(false), 250);
  };

  const goToAnchor = (href, e) => {
    // For anchor links, if not on home page, navigate to home first
    if (href.startsWith("/#")) {
      const anchor = href.substring(2);
      if (location.pathname !== "/") {
        e.preventDefault();
        navigate("/");
        setTimeout(() => {
          const el = document.getElementById(anchor);
          if (el) el.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }
    }
    setOpen(false);
  };

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 inset-x-0 z-40 bg-white shadow-[0_2px_10px_rgba(15,15,25,0.08)] border-b border-neutral-200"
    >
      {/* Top strip */}
      <div className="hidden md:block border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-6 py-2 flex items-center justify-between text-xs">
          <div className="text-neutral-700 flex items-center gap-5">
            <span><i className="fa-regular fa-clock mr-2" />{BRAND.hours}</span>
            <span><i className="fa-solid fa-location-dot mr-2" />{BRAND.address.line1}, {BRAND.address.city}, {BRAND.address.state}</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="font-semibold tracking-wider uppercase text-[color:var(--dbe-red)]">Since {BRAND.since} · {BRAND.yearsOfService}</span>
          </div>
        </div>
      </div>

      {/* Main bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-5 lg:px-6 h-[68px] md:h-[76px] flex items-center justify-between gap-4 md:gap-6">
        <Link to="/" data-testid="header-logo-link" className="flex items-center gap-3 shrink-0">
          <img
            src={BRAND.logo}
            alt="Dead Bug Edwards - The Termite Company"
            className="h-10 md:h-12 w-auto rounded-sm"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
          <Link
            to="/"
            data-testid="nav-link-home"
            className="text-sm font-semibold tracking-wide uppercase text-neutral-900 hover:text-[color:var(--dbe-red)] transition-colors"
          >
            Home
          </Link>

          {/* Services dropdown */}
          <div
            className="relative"
            onMouseEnter={openServices}
            onMouseLeave={scheduleClose}
          >
            <button
              type="button"
              onClick={() => setServicesOpen((v) => !v)}
              data-testid="nav-link-services"
              aria-expanded={servicesOpen}
              className="flex items-center gap-1 text-sm font-semibold tracking-wide uppercase text-neutral-900 hover:text-[color:var(--dbe-red)] transition-colors py-2"
            >
              Services <ChevronDown className={`w-4 h-4 transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
            </button>
            {/* Invisible hover bridge so cursor can move from tab to panel without gap */}
            {servicesOpen && (
              <div
                className="absolute left-1/2 -translate-x-1/2 top-full h-3 w-[560px]"
                aria-hidden="true"
              />
            )}
            {servicesOpen && (
              <div
                data-testid="services-dropdown"
                onMouseEnter={openServices}
                onMouseLeave={scheduleClose}
                className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-[560px] bg-white rounded-2xl shadow-[0_20px_60px_-15px_rgba(15,15,25,0.25)] ring-1 ring-neutral-200 p-3 z-50"
              >
                <Link
                  to="/services"
                  onClick={() => setServicesOpen(false)}
                  data-testid="services-dropdown-all"
                  className="flex items-center justify-between px-4 py-2.5 mb-1 text-sm font-semibold text-[color:var(--dbe-red)] hover:bg-[color:var(--dbe-red)]/5 rounded-xl"
                >
                  All Services
                  <i className="fa-solid fa-arrow-right text-xs" />
                </Link>
                <div className="border-t border-neutral-100 mb-2" />
                <div className="grid grid-cols-2 gap-1">
                  {SERVICES.map((s) => (
                    <Link
                      key={s.slug}
                      to={`/services/${s.slug}`}
                      onClick={() => setServicesOpen(false)}
                      data-testid={`services-dropdown-${s.slug}`}
                      className="flex items-center gap-3 px-3 py-2.5 text-sm text-neutral-800 hover:bg-neutral-50 hover:text-[color:var(--dbe-red)] rounded-xl transition-colors"
                    >
                      <span className="w-8 h-8 rounded-lg bg-[color:var(--dbe-red)]/10 text-[color:var(--dbe-red)] flex items-center justify-center flex-shrink-0">
                        <i className={`fa-solid ${s.icon} text-sm`} />
                      </span>
                      <span className="leading-tight">{s.title}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {navLinks.map((l) =>
            l.type === "route" ? (
              <Link
                key={l.href}
                to={l.href}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className="text-sm font-semibold tracking-wide uppercase text-neutral-900 hover:text-[color:var(--dbe-red)] transition-colors"
              >
                {l.label}
              </Link>
            ) : (
              <a
                key={l.href}
                href={l.href}
                onClick={(e) => goToAnchor(l.href, e)}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className="text-sm font-semibold tracking-wide uppercase text-neutral-900 hover:text-[color:var(--dbe-red)] transition-colors"
              >
                {l.label}
              </a>
            )
          )}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <a
            href={`tel:${BRAND.phoneTel}`}
            data-testid="header-call-link"
            className="hidden md:flex items-center gap-2 font-bebas text-2xl tracking-wide text-[color:var(--dbe-red)]"
          >
            <Phone className="w-5 h-5" strokeWidth={2.5} />
            {BRAND.phoneDisplay}
          </a>
          <a
            href={`tel:${BRAND.phoneTel}`}
            data-testid="mobile-header-call-icon"
            aria-label="Call Dead Bug Edwards"
            className="md:hidden w-10 h-10 rounded-full bg-[color:var(--dbe-red)] text-white flex items-center justify-center shadow-[0_10px_20px_-10px_rgba(227,30,36,0.7)]"
          >
            <Phone className="w-5 h-5" strokeWidth={2.5} />
          </a>
          <a
            href="/#quote"
            onClick={(e) => goToAnchor("/#quote", e)}
            data-testid="header-free-estimate-btn"
            className="hidden md:inline-flex items-center gap-2 bg-[color:var(--dbe-red)] hover:bg-[color:var(--dbe-red-deep)] text-white px-5 py-3 rounded-full text-sm font-bold uppercase tracking-wider transition-transform hover:-translate-y-0.5 shadow-[0_10px_30px_-10px_rgba(227,30,36,0.7)]"
          >
            Free Estimate <i className="fa-solid fa-arrow-right text-xs" />
          </a>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2 rounded-md text-neutral-900"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile sheet */}
      {open && (
        <div className="lg:hidden bg-white border-t border-neutral-200 max-h-[80vh] overflow-y-auto">
          <div className="px-6 py-5 flex flex-col gap-3">
            <Link
              to="/"
              onClick={() => setOpen(false)}
              data-testid="mobile-nav-link-home"
              className="text-neutral-900 font-semibold uppercase text-sm tracking-wider py-1"
            >
              Home
            </Link>
            <details className="group" data-testid="mobile-services-group">
              <summary className="list-none cursor-pointer flex items-center justify-between text-neutral-900 font-semibold uppercase text-sm tracking-wider py-1">
                Services
                <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" />
              </summary>
              <div className="mt-2 pl-4 flex flex-col gap-2">
                <Link
                  to="/services"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-services-all"
                  className="text-[color:var(--dbe-red)] text-sm font-semibold"
                >
                  All Services →
                </Link>
                {SERVICES.map((s) => (
                  <Link
                    key={s.slug}
                    to={`/services/${s.slug}`}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-services-${s.slug}`}
                    className="text-neutral-700 text-sm"
                  >
                    {s.title}
                  </Link>
                ))}
              </div>
            </details>
            {navLinks.map((l) =>
              l.type === "route" ? (
                <Link
                  key={l.href}
                  to={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  className="text-neutral-900 font-semibold uppercase text-sm tracking-wider py-1"
                >
                  {l.label}
                </Link>
              ) : (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={(e) => goToAnchor(l.href, e)}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  className="text-neutral-900 font-semibold uppercase text-sm tracking-wider py-1"
                >
                  {l.label}
                </a>
              )
            )}
            <a
              href={`tel:${BRAND.phoneTel}`}
              className="font-bebas text-2xl text-[color:var(--dbe-red)] py-1"
              data-testid="mobile-call-link"
            >
              <Phone className="inline w-5 h-5 mr-2" />{BRAND.phoneDisplay}
            </a>
            <a
              href="/#quote"
              onClick={(e) => goToAnchor("/#quote", e)}
              data-testid="mobile-free-estimate-btn"
              className="w-full text-center bg-[color:var(--dbe-red)] text-white py-3 rounded-full font-bold uppercase tracking-wider"
            >
              Free Estimate
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
